import type { LspMessageLike, LspTransport } from "./types.ts";

type RequestHandler = (params: unknown) => unknown;
type NotificationHandler = (params: unknown) => void;

export class LspRequestError extends Error {
  readonly code: number;
  constructor(code: number, message: string) {
    super(message);
    this.code = code;
  }
}

export const REQUEST_CANCELLED = -32800;

/** Minimal JSON-RPC 2.0 peer over an LspTransport: requests with timeouts/cancellation, notifications, server requests. */
export class JsonRpcPeer {
  private nextId = 1;
  private readonly pending = new Map<number | string, { resolve: (v: unknown) => void; reject: (e: Error) => void; timer: ReturnType<typeof setTimeout> }>();
  private readonly requestHandlers = new Map<string, RequestHandler>();
  private readonly notificationHandlers = new Map<string, NotificationHandler>();
  private readonly transport: LspTransport;
  private readonly offs: Array<() => void> = [];
  private disposed = false;

  constructor(transport: LspTransport) {
    this.transport = transport;
    this.offs.push(transport.onMessage((m) => void this.onMessage(m)));
    this.offs.push(transport.onClose((reason) => this.dispose(reason)));
  }

  get isDisposed(): boolean {
    return this.disposed;
  }

  onRequest(method: string, handler: RequestHandler): void {
    this.requestHandlers.set(method, handler);
  }
  onNotification(method: string, handler: NotificationHandler): void {
    this.notificationHandlers.set(method, handler);
  }

  request<T>(method: string, params: unknown, opts: { timeoutMs?: number; signal?: AbortSignal } = {}): Promise<T> {
    if (this.disposed) return Promise.reject(new LspRequestError(-32001, "language server connection closed"));
    const id = this.nextId++;
    return new Promise<T>((resolve, reject) => {
      const timer = setTimeout(() => {
        if (this.pending.delete(id)) {
          this.transport.send({ jsonrpc: "2.0", method: "$/cancelRequest", params: { id } });
          reject(new LspRequestError(-32002, `${method} timed out`));
        }
      }, opts.timeoutMs ?? 30_000);
      this.pending.set(id, { resolve: resolve as (v: unknown) => void, reject, timer });
      opts.signal?.addEventListener(
        "abort",
        () => {
          if (!this.pending.delete(id)) return;
          clearTimeout(timer);
          this.transport.send({ jsonrpc: "2.0", method: "$/cancelRequest", params: { id } });
          reject(new LspRequestError(REQUEST_CANCELLED, "cancelled"));
        },
        { once: true },
      );
      this.transport.send({ jsonrpc: "2.0", id, method, params });
    });
  }

  notify(method: string, params: unknown): void {
    if (this.disposed) return;
    this.transport.send({ jsonrpc: "2.0", method, params });
  }

  dispose(reason = "closed"): void {
    if (this.disposed) return;
    this.disposed = true;
    for (const off of this.offs) off();
    for (const [id, p] of this.pending) {
      clearTimeout(p.timer);
      this.pending.delete(id);
      p.reject(new LspRequestError(-32001, `language server connection closed (${reason})`));
    }
  }

  private async onMessage(m: LspMessageLike): Promise<void> {
    if (m.method !== undefined) {
      if (m.id !== undefined && m.id !== null) await this.onServerRequest(m);
      else this.notificationHandlers.get(m.method)?.(m.params);
      return;
    }
    if (m.id === undefined || m.id === null) return;
    const p = this.pending.get(m.id);
    if (!p) return;
    this.pending.delete(m.id);
    clearTimeout(p.timer);
    if (m.error) p.reject(new LspRequestError(m.error.code, m.error.message));
    else p.resolve(m.result ?? null);
  }

  private async onServerRequest(m: LspMessageLike): Promise<void> {
    const handler = this.requestHandlers.get(m.method!);
    try {
      const result = handler ? await handler(m.params) : null;
      this.transport.send({ jsonrpc: "2.0", id: m.id!, result: result ?? null });
    } catch (err) {
      this.transport.send({
        jsonrpc: "2.0",
        id: m.id!,
        error: { code: -32603, message: err instanceof Error ? err.message : "request failed" },
      });
    }
  }
}
